import React, { useCallback, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import OperationHealthCard from "../components/OperationHealthCard";
import NextBestActionCard from "../components/NextBestActionCard";
import CurrentMissionCard from "../components/CurrentMissionCard";
import RegisterSaleModal from "../components/RegisterSaleModal";
import RegisterLeadModal from "../components/RegisterLeadModal";
import ModoLiveUnlockModal from "../components/ModoLiveUnlockModal";
import AssumeRiskModal from "../components/AssumeRiskModal";
import TimelineCard from "../components/TimelineCard";
import {
  Flame,
  Zap,
  DollarSign,
  TrendingUp,
  MessageSquare,
  Clock,
  Plus,
  Bell,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  ChevronRight,
  Calendar,
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get("/api/operations/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 15000); // refresh every 15s
    return () => clearInterval(interval);
  }, [user, fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-semibold text-gray-300">Carregando Modo Caverna...</span>
        </div>
      </div>
    );
  }

  const operation = data?.operation || {};
  const campaign = data?.campaign;
  const timers = data?.timers || {};
  const notifications = data?.notifications || [];
  const mode = operation?.mode || "MODO_CAVERNA";
  const stage = operation?.stage || "CAVE_PROFILE";

  // Time-aware Brazilian greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = user?.name?.split(" ")[0] || "Operador";
    if (hour >= 5 && hour < 12) return `Bom dia, ${firstName} 👊`;
    if (hour >= 12 && hour < 18) return `Boa tarde, ${firstName} 👊`;
    return `Boa noite, ${firstName} 👊`;
  };

  const totalSpend = operation?.total_spend || 0;
  const totalRev = operation?.total_revenue || 0;
  const roi = totalSpend > 0 ? (totalRev / totalSpend).toFixed(2) : "0.00";

  // Format currency helper
  const fmtBRL = (val) =>
    `R$ ${Number(val || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="dashboard-container" className="space-y-4 pb-20 max-w-5xl mx-auto px-3 sm:px-4 pt-3">
      {/* 1. Header Greeting & Quick Streaks */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
        <div>
          <h1 data-testid="user-greeting" className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
            {getGreeting()}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span className="font-semibold text-gray-300">
              {mode === "MODO_CAVERNA" ? "Modo Caverna (Preparação)" : "Modo Live (Tráfego Ativo)"}
            </span>
            <span>•</span>
            <span>Nível {operation?.level || 1} — {operation?.level_name || "Recruta"}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <div
            data-testid="operational-streak-badge"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1D212C] border border-[#282D3D] text-xs shrink-0"
          >
            <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
            <span className="font-extrabold text-white">{operation?.operational_streak_days || 1}</span>
            <span className="text-gray-400">dias operando</span>
          </div>

          <div
            data-testid="sales-streak-badge"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1D212C] border border-[#282D3D] text-xs shrink-0"
          >
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <span className="font-extrabold text-emerald-400">{operation?.sales_streak_days || 0}</span>
            <span className="text-gray-400">dias c/ venda</span>
          </div>

          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1D212C] border border-[#282D3D] text-xs shrink-0">
            <Zap className="w-4 h-4 text-[#FF6B00] fill-[#FF6B00]" />
            <span className="font-extrabold text-[#FF6B00]">{operation?.xp || 100}</span>
            <span className="text-gray-400">XP</span>
          </div>
        </div>
      </div>

      {/* 2. CORE UX: SAÚDE DA OPERAÇÃO (🚨 Metaphor) */}
      <OperationHealthCard operation={operation} />

      {/* 3. CORE UX: PRÓXIMA AÇÃO (Single Focus Mentor Recommendation) */}
      <NextBestActionCard
        actionText={operation?.next_best_action}
        ctaLabel={
          stage === "CAVE_UNLOCK"
            ? "Desbloquear Modo Live"
            : stage === "LIVE_PRETEST" && campaign?.leads_by_stage?.valores >= 3
            ? "Ir Para Teste"
            : stage === "LIVE_TEST" && operation?.total_sales_count > 0
            ? "Entrar na Pré-Escala"
            : stage === "LIVE_PRESCALE" && timers?.prescale_status === "CRITICAL"
            ? "Avaliar Assumir Risco"
            : stage === "LIVE_SCALE" && timers?.scale_budget_can_increase
            ? "Aumentar +20%"
            : "Ver Missão Completa"
        }
        onActionClick={() => {
          if (stage === "CAVE_UNLOCK") setShowUnlockModal(true);
          else if (stage === "LIVE_PRESCALE" && timers?.prescale_status === "CRITICAL") setShowRiskModal(true);
        }}
        linkTo={
          stage === "CAVE_UNLOCK" || (stage === "LIVE_PRESCALE" && timers?.prescale_status === "CRITICAL")
            ? null
            : "/missoes"
        }
      />

      {/* 4. CURRENT MISSION & STAGE PROGRESS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <CurrentMissionCard
            mission={operation?.current_mission}
            stage={stage}
            mode={mode}
            onOpenMission={() => {}}
          />
        </div>

        {/* Quick Operational Metrics */}
        <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 flex flex-col justify-between">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center justify-between">
            <span>Métricas da Operação</span>
            <span className="text-[10px] text-[#FF6B00]">Tempo Real</span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-[#1A1D26] p-2 rounded-lg border border-[#282D3D]">
              <span className="text-gray-400 text-[11px] block">Investimento</span>
              <span data-testid="metric-total-spend" className="text-sm font-extrabold text-white">
                {fmtBRL(totalSpend)}
              </span>
            </div>

            <div className="bg-[#1A1D26] p-2 rounded-lg border border-[#282D3D]">
              <span className="text-gray-400 text-[11px] block">Receita Total</span>
              <span data-testid="metric-total-revenue" className="text-sm font-extrabold text-emerald-400">
                {fmtBRL(totalRev)}
              </span>
            </div>

            <div className="bg-[#1A1D26] p-2 rounded-lg border border-[#282D3D]">
              <span className="text-gray-400 text-[11px] block">ROI Atual</span>
              <span data-testid="metric-roi" className="text-sm font-extrabold text-[#FF6B00]">
                {roi}x
              </span>
            </div>

            <div className="bg-[#1A1D26] p-2 rounded-lg border border-[#282D3D]">
              <span className="text-gray-400 text-[11px] block">Total de Vendas</span>
              <span data-testid="metric-total-sales" className="text-sm font-extrabold text-white">
                {operation?.total_sales_count || 0}
              </span>
            </div>
          </div>

          <Link
            to="/operacao"
            data-testid="view-full-operation-btn"
            className="mt-3 text-center text-xs font-bold text-[#FF6B00] hover:text-[#FF8533] pt-2 border-t border-[#232733] flex items-center justify-center gap-1"
          >
            <span>Ver Painel Completo de Operação</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* 5. STAGE SPECIFIC LIVE STATUS BANNERS */}
      {mode === "MODO_LIVE" && (
        <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-extrabold uppercase tracking-wider text-white flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[#FF6B00]" />
              <span>Status Operacional em Tempo Real</span>
            </span>
            <span className="text-xs text-gray-400">
              Campanha: {campaign?.name || "Campanha Ativa"}
            </span>
          </div>

          {/* Pré-Teste Leads Goal */}
          {stage === "LIVE_PRETEST" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-gray-300">LEADS QUALIFICADOS EM VALORES</span>
                <span data-testid="pretest-leads-progress" className="text-sm font-extrabold text-[#FF6B00]">
                  {campaign?.leads_by_stage?.valores || 0} / 3 no WhatsApp
                </span>
              </div>
              <div className="w-full h-3 bg-[#1F2430] rounded-full overflow-hidden border border-[#282D3D]">
                <div
                  className="h-full bg-gradient-to-r from-yellow-500 to-[#FF6B00] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, ((campaign?.leads_by_stage?.valores || 0) / 3) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400">
                Orçamento recomendado: R$ 30/dia. O teste será liberado assim que 3 leads chegarem na etapa de valores.
              </p>
            </div>
          )}

          {/* Teste Day 1/2/3 */}
          {stage === "LIVE_TEST" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex gap-2 text-xs font-bold">
                  {[1, 2, 3].map((d) => (
                    <span
                      key={d}
                      className={`px-3 py-1 rounded-lg border ${
                        timers.test_day === d
                          ? "bg-[#FF6B00] text-black font-extrabold border-[#FF6B00]"
                          : timers.test_day > d
                          ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                          : "bg-[#1A1D26] text-gray-500 border-[#282D3D]"
                      }`}
                    >
                      DIA {d} DE 3
                    </span>
                  ))}
                </div>
                <span className="text-xs text-gray-400">Orçamento: R$ 72/dia</span>
              </div>

              {timers.test_day === 1 && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-300 font-medium">
                  🟢 <strong>Dia 1 — Coleta de dados:</strong> Não tome decisões sobre a campanha hoje. Continue atendendo os leads e registre os resultados.
                </div>
              )}
              {timers.test_day === 2 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-300 font-medium">
                  🟡 <strong>Dia 2 — Observação:</strong> Já conseguimos enxergar tendências de conversas. Mantenha a campanha ativa sem alterações precipitadas.
                </div>
              )}
              {timers.test_day === 3 && (
                <div className="p-3 bg-[#FF6B00]/15 border border-[#FF6B00]/40 rounded-lg text-xs text-white font-medium flex items-center justify-between">
                  <div>
                    🧠 <strong>Dia 3 — Diagnóstico:</strong> Hora de avaliar vendas reais geradas e validar avanço para Pré-Escala.
                  </div>
                  <Link
                    to="/missoes"
                    className="px-3 py-1.5 bg-[#FF6B00] text-black font-extrabold rounded text-xs shrink-0 ml-2"
                  >
                    Ver Diagnóstico
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Pré-Escala 48h Timer */}
          {stage === "LIVE_PRESCALE" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-gray-300">Tempo para 1ª Venda na Pré-Escala</span>
                <span data-testid="prescale-timer-text" className="font-mono text-sm font-extrabold text-[#FF6B00]">
                  ⏱️ {Math.min(48, Math.round(timers.prescale_first_sale_hours_elapsed || 0))}h / 48h
                </span>
              </div>

              <div className="w-full h-2.5 bg-[#1F2430] rounded-full overflow-hidden border border-[#282D3D]">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    timers.prescale_status === "CRITICAL" ? "bg-red-500" : "bg-[#FF6B00]"
                  }`}
                  style={{
                    width: `${Math.min(100, ((timers.prescale_first_sale_hours_elapsed || 0) / 48) * 100)}%`,
                  }}
                />
              </div>

              {timers.prescale_status === "CRITICAL" && (
                <div className="p-3 bg-red-500/15 border border-red-500/50 rounded-lg text-xs text-red-300 flex items-center justify-between">
                  <div>
                    🚨 <strong>DESEMPENHO CRÍTICO:</strong> Campanha ultrapassou 48h sem venda. Se o motivo foi falha de atendimento, você pode assumir o risco (+2 dias).
                  </div>
                  <button
                    data-testid="open-risk-modal-btn"
                    onClick={() => setShowRiskModal(true)}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white font-extrabold rounded text-xs shrink-0 ml-2 shadow-[0_0_10px_rgba(239,68,68,0.4)]"
                  >
                    Assumir Risco
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Escala 24h 20% Increase Countdown */}
          {stage === "LIVE_SCALE" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-white">Disciplina de Escala (+20% a cada 24h)</span>
                <span className="text-gray-400">
                  Orçamento atual: {fmtBRL(timers.scale_current_budget)}
                </span>
              </div>

              {timers.scale_budget_can_increase ? (
                <div className="p-3 bg-emerald-500/15 border border-emerald-500/40 rounded-lg text-xs text-emerald-300 flex items-center justify-between">
                  <div>
                    🚀 <strong>AUMENTO LIBERADO:</strong> Orçamento pronto para subir para {fmtBRL(timers.scale_next_budget)} (+20%).
                  </div>
                  <Link
                    to="/operacao"
                    className="px-3 py-1.5 bg-emerald-500 text-black font-extrabold rounded text-xs shrink-0 ml-2"
                  >
                    Aplicar Aumento
                  </Link>
                </div>
              ) : (
                <div className="p-3 bg-[#1A1D26] border border-[#282D3D] rounded-lg text-xs text-gray-300 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-yellow-400">
                    <Clock className="w-4 h-4" />
                    <span>Próximo aumento liberado em: {timers.scale_budget_timer_hours_left}h</span>
                  </span>
                  <span className="text-gray-400 font-mono">🔒 Bloqueado</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 6. Actionable Notification Alerts */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map((notif, idx) => (
            <div
              key={notif.id || notif.key || `notif-${notif.title}-${idx}`}
              className="bg-[#14161C] border border-[#282D3D] rounded-xl p-3 flex items-start gap-3 text-xs"
            >
              <div className="w-7 h-7 rounded-lg bg-[#FF6B00]/15 flex items-center justify-center text-[#FF6B00] shrink-0">
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div>
                <div className="font-bold text-white">{notif.title}</div>
                <div className="text-gray-300 mt-0.5">{notif.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 6b. OPERATIONAL TIMELINE (real persisted events) */}
      <TimelineCard events={data?.timeline || []} />

      {/* 7. FLOATING ACTION BUTTONS (Mobile-first fast access) */}
      <div className="fixed bottom-16 md:bottom-6 right-4 z-40 flex flex-col gap-2.5">
        <button
          data-testid="floating-register-sale-btn"
          onClick={() => setShowSaleModal(true)}
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_4px_25px_rgba(16,185,129,0.5)] transition-transform active:scale-95"
        >
          <DollarSign className="w-4 h-4 stroke-[3]" />
          <span>+ Registrar Venda</span>
        </button>

        <button
          data-testid="floating-register-lead-btn"
          onClick={() => setShowLeadModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1F2432] hover:bg-[#282F42] border border-[#3E4760] text-white font-bold text-xs shadow-[0_4px_20px_rgba(0,0,0,0.6)] transition-transform active:scale-95"
        >
          <MessageSquare className="w-3.5 h-3.5 text-[#FF6B00]" />
          <span>+ Registrar Lead</span>
        </button>
      </div>

      {/* Modals */}
      <RegisterSaleModal
        isOpen={showSaleModal}
        onClose={() => setShowSaleModal(false)}
        onSaleSuccess={fetchDashboard}
        activeCampaignId={campaign?._id || campaign?.id}
        preselectedCreativeId={campaign?.creative_id}
      />

      <RegisterLeadModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onLeadSuccess={fetchDashboard}
        activeCampaignId={campaign?._id || campaign?.id}
      />

      <ModoLiveUnlockModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onUnlocked={fetchDashboard}
      />

      <AssumeRiskModal
        isOpen={showRiskModal}
        onClose={() => setShowRiskModal(false)}
        campaignId={campaign?._id || campaign?.id}
        onSuccess={fetchDashboard}
      />
    </div>
  );
}
